import { useState, useEffect } from "react";
import Sidebar from "../sidebar/Sidebar";

import {
  FiAlertCircle,
  FiBook,
  FiCalendar,
  FiInfo,
  FiClock,
  FiUser,
} from "react-icons/fi";
const apiUrl = process.env.REACT_APP_API_URL;
const NoticeBoard = () => {
    ///console.log("api ",apiUrl)
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiUrl}/api/notices`);
        if (!response.ok) {
          throw new Error("Failed to fetch notices");
        }
        const data = await response.json();
        if (data.success) {
          setNotices(data.data);
        } else {
          throw new Error("No data received");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const getCategoryColor = (category) => {
    const categoryLower = category.toLowerCase();
    switch (categoryLower) {
      case "important":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          text: "text-red-800",
          icon: <FiAlertCircle className="text-red-500 mr-1" />,
          button: "bg-red-100 hover:bg-red-200 text-red-800",
        };
      case "academic":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          text: "text-blue-800",
          icon: <FiBook className="text-blue-500 mr-1" />,
          button: "bg-blue-100 hover:bg-blue-200 text-blue-800",
        };
      case "event":
        return {
          bg: "bg-green-50",
          border: "border-green-200",
          text: "text-green-800",
          icon: <FiCalendar className="text-green-500 mr-1" />,
          button: "bg-green-100 hover:bg-green-200 text-green-800",
        };
      case "general":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          text: "text-yellow-800",
          icon: <FiInfo className="text-yellow-500 mr-1" />,
          button: "bg-yellow-100 hover:bg-yellow-200 text-yellow-800",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          text: "text-gray-800",
          icon: <FiInfo className="text-gray-500 mr-1" />,
          button: "bg-gray-100 hover:bg-gray-200 text-gray-800",
        };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredNotices = notices.filter((notice) => {
    const matchesCategory =
      activeCategory === "all" ||
      notice.category.toLowerCase() === activeCategory;
    const matchesSearch =
      notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notice.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex flex-col items-center justify-center flex-1 w-full max-w-6xl px-4 py-8 mx-auto">
          <div className="flex space-x-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex flex-col items-center">
                <div className="h-12 w-12 rounded-full bg-blue-100 mb-2"></div>
                <div className="h-4 w-16 bg-blue-100 rounded"></div>
              </div>
            ))}
          </div>
          <div className="mt-8 w-full max-w-2xl">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-blue-100 rounded w-3/4"></div>
              <div className="h-4 bg-blue-100 rounded"></div>
              <div className="h-4 bg-blue-100 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 p-8">
          <div
            className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-sm"
            role="alert"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <FiAlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading notices
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-2">
              📝 Notice Board
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay updated with the latest announcements and important
              information
            </p>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search notices..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveCategory("all")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${
                    activeCategory === "all"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  All Notices
                </button>
                <button
                  onClick={() => setActiveCategory("important")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${
                    activeCategory === "important"
                      ? "bg-red-600 text-white shadow-md"
                      : "bg-red-100 text-red-800 hover:bg-red-200"
                  }`}
                >
                  <FiAlertCircle className="mr-1" /> Important
                </button>
                <button
                  onClick={() => setActiveCategory("academic")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${
                    activeCategory === "academic"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                  }`}
                >
                  <FiBook className="mr-1" /> Academic
                </button>
                <button
                  onClick={() => setActiveCategory("event")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${
                    activeCategory === "event"
                      ? "bg-green-600 text-white shadow-md"
                      : "bg-green-100 text-green-800 hover:bg-green-200"
                  }`}
                >
                  <FiCalendar className="mr-1" /> Events
                </button>
                <button
                  onClick={() => setActiveCategory("general")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${
                    activeCategory === "general"
                      ? "bg-yellow-600 text-white shadow-md"
                      : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                  }`}
                >
                  <FiInfo className="mr-1" /> General
                </button>
              </div>
            </div>
          </div>

          {/* Notices List */}
          {filteredNotices.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <FiInfo className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                {activeCategory === "all"
                  ? "No notices available"
                  : `No ${activeCategory} notices found`}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? "Try adjusting your search or filter to find what you're looking for."
                  : "Check back later for new announcements."}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredNotices.map((notice) => {
                const categoryStyle = getCategoryColor(notice.category);
                return (
                  <div
                    key={notice._id}
                    className={`border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 ${categoryStyle.bg} ${categoryStyle.border}`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-2">
                        <div>
                          <div className="flex items-center mb-1">
                            {categoryStyle.icon}
                            <span
                              className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${categoryStyle.button}`}
                            >
                              {notice.category}
                            </span>
                          </div>
                          <h2 className="text-xl font-bold text-gray-900">
                            {notice.title}
                          </h2>
                        </div>
                        <div className="text-sm text-gray-500 whitespace-nowrap">
                          <FiClock className="inline mr-1" />{" "}
                          {formatDate(notice.createdAt)}
                        </div>
                      </div>

                      <div className="prose max-w-none text-gray-700 mb-6 whitespace-pre-line">
                        {notice.content}
                      </div>

                      <div className="border-t pt-4 flex flex-col sm:flex-row sm:justify-between gap-2">
                        <div className="text-sm text-gray-600">
                          {notice.createdBy && (
                            <p className="flex items-center">
                              <FiUser className="mr-1" /> Posted by:{" "}
                              {notice.createdBy}
                            </p>
                          )}
                        </div>
                        {notice.expiryDate && (
                          <div className="text-sm text-gray-600">
                            <p className="flex items-center">
                              <FiClock className="mr-1" /> Expires:{" "}
                              {formatDate(notice.expiryDate)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticeBoard;
